import React from "react";
import ReportShell from "@/components/ReportShell";
import { Field, TextInput, TextArea } from "@/components/ReportPrimitives";
import { buildBitRunReport, bitReviewChecklist, BIT_QUICK_PHRASES } from "@/lib/reports/bit";
import { bitStore } from "@/lib/reports/storage";
import { useReportRceWiring } from "@/lib/rce/useReportRceWiring";

const emptyState = () => ({
  id: null,
  header: {},
  bit: {},
  bha: {},
  run: {},
  params: {},
  performance: {},
  pull: {},
  notes: {},
  recommendations: {},
});

function QuickPhrases({ onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {BIT_QUICK_PHRASES.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          className="text-[10.5px] px-2 py-1 rounded-md"
          style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)" }}
        >
          + {p}
        </button>
      ))}
    </div>
  );
}

export default function BitRunRecord() {
  const rce = useReportRceWiring("bitRunRecord");
  const sections = [
    {
      id: "header", badge: "01", title: "Report Header", defaultOpen: true,
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Date"><TextInput type="date" value={state.header.reportDate} onChange={(v) => set("header", "reportDate", v)} testId="bit-h-reportDate" /></Field>
          <Field label="Rig Name"><TextInput value={state.header.rigName} onChange={(v) => set("header", "rigName", v)} testId="bit-h-rigName" /></Field>
          <Field label="Well Name"><TextInput value={state.header.wellName} onChange={(v) => set("header", "wellName", v)} testId="bit-h-wellName" /></Field>
          <Field label="Operator / Client"><TextInput value={state.header.operator} onChange={(v) => set("header", "operator", v)} testId="bit-h-operator" /></Field>
          <Field label="Prepared By"><TextInput value={state.header.preparedBy} onChange={(v) => set("header", "preparedBy", v)} testId="bit-h-preparedBy" /></Field>
        </div>
      ),
    },
    {
      id: "bit", badge: "02", title: "Bit Details",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Bit Number"><TextInput value={state.bit.bitNumber} onChange={(v) => set("bit", "bitNumber", v)} testId="bit-b-bitNumber" /></Field>
          <Field label="Bit Size"><TextInput value={state.bit.bitSize} onChange={(v) => set("bit", "bitSize", v)} testId="bit-b-bitSize" /></Field>
          <Field label="Bit Type"><TextInput value={state.bit.bitType} onChange={(v) => set("bit", "bitType", v)} placeholder="PDC / Tricone …" testId="bit-b-bitType" /></Field>
          <Field label="Manufacturer"><TextInput value={state.bit.manufacturer} onChange={(v) => set("bit", "manufacturer", v)} testId="bit-b-manufacturer" /></Field>
          <Field label="IADC Code"><TextInput value={state.bit.iadcCode} onChange={(v) => set("bit", "iadcCode", v)} testId="bit-b-iadcCode" /></Field>
          <Field label="Serial Number"><TextInput value={state.bit.serial} onChange={(v) => set("bit", "serial", v)} testId="bit-b-serial" /></Field>
          <Field label="Jets / Nozzles"><TextInput value={state.bit.jets} onChange={(v) => set("bit", "jets", v)} testId="bit-b-jets" /></Field>
          <Field label="TFA"><TextInput value={state.bit.tfa} onChange={(v) => set("bit", "tfa", v)} testId="bit-b-tfa" /></Field>
        </div>
      ),
    },
    {
      id: "bha", badge: "03", title: "BHA Summary",
      render: (state, set) => (
        <>
          <Field label="BHA Description"><TextArea value={state.bha.description} onChange={(v) => set("bha", "description", v)} rows={3} testId="bit-bha-description" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Motor / RSS Details"><TextInput value={state.bha.motorRss} onChange={(v) => set("bha", "motorRss", v)} testId="bit-bha-motorRss" /></Field>
            <Field label="MWD / LWD Tools"><TextInput value={state.bha.mwdLwd} onChange={(v) => set("bha", "mwdLwd", v)} testId="bit-bha-mwdLwd" /></Field>
            <Field label="Stabilizers"><TextInput value={state.bha.stabilizers} onChange={(v) => set("bha", "stabilizers", v)} testId="bit-bha-stabilizers" /></Field>
          </div>
        </>
      ),
    },
    {
      id: "run", badge: "04", title: "Run Summary",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Run In Depth"><TextInput type="number" value={state.run.runInDepth} onChange={(v) => set("run", "runInDepth", v)} testId="bit-r-runInDepth" /></Field>
          <Field label="Pull Out Depth"><TextInput type="number" value={state.run.pullOutDepth} onChange={(v) => set("run", "pullOutDepth", v)} testId="bit-r-pullOutDepth" /></Field>
          <Field label="Interval Drilled"><TextInput value={state.run.intervalDrilled} onChange={(v) => set("run", "intervalDrilled", v)} testId="bit-r-intervalDrilled" /></Field>
          <Field label="Bit Hours"><TextInput type="number" value={state.run.bitHours} onChange={(v) => set("run", "bitHours", v)} testId="bit-r-bitHours" /></Field>
          <Field label="Rotating Hours"><TextInput type="number" value={state.run.rotatingHours} onChange={(v) => set("run", "rotatingHours", v)} testId="bit-r-rotatingHours" /></Field>
        </div>
      ),
    },
    {
      id: "params", badge: "05", title: "Drilling Parameters",
      render: (state, set) => (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="WOB"><TextInput type="number" value={state.params.wob} onChange={(v) => set("params", "wob", v)} testId="bit-p-wob" /></Field>
          <Field label="RPM"><TextInput type="number" value={state.params.rpm} onChange={(v) => set("params", "rpm", v)} testId="bit-p-rpm" /></Field>
          <Field label="Torque"><TextInput type="number" value={state.params.torque} onChange={(v) => set("params", "torque", v)} testId="bit-p-torque" /></Field>
          <Field label="Flow Rate"><TextInput type="number" value={state.params.flowRate} onChange={(v) => set("params", "flowRate", v)} testId="bit-p-flowRate" /></Field>
          <Field label="SPP"><TextInput type="number" value={state.params.spp} onChange={(v) => set("params", "spp", v)} testId="bit-p-spp" /></Field>
          <Field label="Mud Weight"><TextInput type="number" value={state.params.mudWeight} onChange={(v) => set("params", "mudWeight", v)} testId="bit-p-mudWeight" /></Field>
        </div>
      ),
    },
    {
      id: "performance", badge: "06", title: "Performance",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Average ROP"><TextInput value={state.performance.avgRop} onChange={(v) => set("performance", "avgRop", v)} testId="bit-perf-avgRop" /></Field>
          <Field label="Formation Drilled"><TextInput value={state.performance.formation} onChange={(v) => set("performance", "formation", v)} testId="bit-perf-formation" /></Field>
        </div>
      ),
    },
    {
      id: "pull", badge: "07", title: "Dull Grade / Pull Reason",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Reason Pulled"><TextInput value={state.pull.reason} onChange={(v) => set("pull", "reason", v)} testId="bit-pull-reason" /></Field>
          <Field label="Dull Grade (if available)"><TextInput value={state.pull.dullGrade} onChange={(v) => set("pull", "dullGrade", v)} testId="bit-pull-dullGrade" /></Field>
        </div>
      ),
    },
    {
      id: "notes", badge: "08", title: "Lessons Learned / Recommendations",
      render: (state, set, helpers) => (
        <>
          <Field label="Rough Notes"><TextArea value={state.notes.roughNotes} onChange={(v) => set("notes", "roughNotes", v)} rows={3} testId="bit-n-roughNotes" /></Field>
          <QuickPhrases onPick={(p) => {
            helpers.setState((s) => ({ ...s, notes: { ...s.notes, roughNotes: ((s.notes.roughNotes || "") + (s.notes.roughNotes && !/\s$/.test(s.notes.roughNotes || "") ? " " : "") + p) } }));
          }} testId="bit-n-quick-phrases" />
          <Field label="Recommendations"><TextArea value={state.recommendations.text} onChange={(v) => set("recommendations", "text", v)} rows={3} testId="bit-r-recommendations" /></Field>
        </>
      ),
    },
  ];

  return (
    <ReportShell
      testIdPrefix="bit"
      reportType="bitRunRecord"
      safetyCritical={false}
      title="Bit Run Record"
      subtitle="Bit details, BHA, run, performance, dull grade"
      brandLine="Bit Run Record  ·  Draft Documentation Support"
      emptyState={emptyState}
      buildFn={buildBitRunReport}
      checklistFn={bitReviewChecklist}
      store={bitStore}
      sections={sections}
      initialState={rce.initialState}
      rceContextStrip={rce.rceContextStrip}
      filenameFromState={(s) => {
        const safe = (v) => (v || "").toString().replace(/[^a-z0-9-_]+/gi, "_").slice(0, 30);
        return `RigSentinel_BitRun_${safe(s.header.wellName) || "well"}_${(s.header.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`;
      }}
    />
  );
}
